import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';
import usePage from '@dashboard/hooks/usePage';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';

export default function CustomersList({ sort, filter = {}, search, sorting, onSortingChange }) {
  const { page } = usePage();
  const { sellerId } = useAuth();
  const { getCustomersByCheckout } = useCustomerQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['pageCustomers', sellerId, sort, search, filter, page._id],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getCustomersByCheckout(page._id, {
          ...filter,
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  let title = 'customers';
  if (page.type === 'form') {
    title = 'leads';
  } else if (page.type === 'event') {
    title = 'guests';
  }

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
        cell: ({ row }) => {
          const customer = row.original;
          return (
            <div>
              <div className="font-medium">{customer.email || ''}</div>
              <div className="text-sm text-gray-500 dark:text-muted-foreground">
                {customer.name || ''}
              </div>
            </div>
          );
        },
        enableSorting: true,
        meta: { className: 'w-full' },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataGridColumnHeader column={column} title="Created date" className="justify-end" />
        ),
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm'),
        enableSorting: true,
        meta: { className: 'text-right whitespace-nowrap' },
      },
    ],
    [],
  );

  if (isError) {
    return <div className="m-4">Unable to show {title}. Please refresh or contact support.</div>;
  }

  const isEmpty = !isLoading && flatData.length === 0;

  const emptyState = isEmpty ? (
    <EmptyState>
      <EmptyState.Title>No {title} yet</EmptyState.Title>
      <EmptyState.Subtitle>Your {title} will show up here.</EmptyState.Subtitle>
    </EmptyState>
  ) : undefined;

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode="infinite"
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sorting}
      onSortingChange={onSortingChange}
      getRowHref={customer => `/customers/${customer._id}`}
      emptyState={emptyState}
      emptyMessage={`No ${title} found`}
    />
  );
}
